import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchSignalAlertsLive,
  fetchSignalAlertsHistory,
  fetchSignalAlertsDates,
  fetchSignalAlertsSummary,
} from '../api';

const fmt = (n, dec = 2) => {
  if (n === undefined || n === null || n === '') return '—';
  const num = Number(n);
  if (!isFinite(num)) return '—';
  return num.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};
const fmtInt = (n) => (n ? Math.round(Number(n)).toLocaleString('en-IN') : '—');
const fmtSigned = (n) => {
  if (n === undefined || n === null) return '—';
  const num = Number(n);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}`;
};
const timeOnly = (s) => (s ? s.split(' ')[1] || s : '—');

function DirectionBadge({ dir }) {
  const isBull = dir === 'BULLISH';
  return (
    <span className={`sa-dir ${isBull ? 'sa-dir-bull' : 'sa-dir-bear'}`}>
      {isBull ? '▲ BULLISH' : '▼ BEARISH'}
    </span>
  );
}

function PnlCell({ value, suffix = '' }) {
  if (value === undefined || value === null) return <span>—</span>;
  const num = Number(value);
  const cls = num > 0 ? 'sa-pos' : num < 0 ? 'sa-neg' : 'sa-zero';
  return <span className={cls}>{fmtSigned(num)}{suffix}</span>;
}

export default function SignalAlerts() {
  const [tab, setTab] = useState('live'); // live | history
  const [live, setLive] = useState(null);
  const [liveErr, setLiveErr] = useState(null);

  // History controls
  const today = new Date().toISOString().split('T')[0];
  const lastWeek = new Date(Date.now() - 7 * 86400_000).toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(lastWeek);
  const [toDate, setToDate] = useState(today);
  const [direction, setDirection] = useState('');
  const [histRows, setHistRows] = useState([]);
  const [histSummary, setHistSummary] = useState(null);
  const [histDates, setHistDates] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histErr, setHistErr] = useState(null);

  // ── Live polling ─────────────────────────────────────────
  const loadLive = useCallback(async () => {
    try {
      const { data } = await fetchSignalAlertsLive();
      if (data?.status === 'ok') {
        setLive(data);
        setLiveErr(null);
      } else {
        setLiveErr('Failed to load live alerts');
      }
    } catch (e) {
      setLiveErr(e.message || 'Network error');
    }
  }, []);

  useEffect(() => {
    if (tab !== 'live') return;
    loadLive();
    const id = setInterval(loadLive, 15000);
    return () => clearInterval(id);
  }, [tab, loadLive]);

  // ── Historical load ──────────────────────────────────────
  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    setHistErr(null);
    try {
      const params = { from_date: fromDate, to_date: toDate };
      if (direction) params.direction = direction;
      const [hRes, sRes, dRes] = await Promise.all([
        fetchSignalAlertsHistory(params),
        fetchSignalAlertsSummary({ from_date: fromDate, to_date: toDate }),
        fetchSignalAlertsDates(),
      ]);
      setHistRows(hRes.data?.data || []);
      setHistSummary(sRes.data?.status === 'ok' ? sRes.data : null);
      setHistDates(dRes.data?.data || []);
    } catch (e) {
      setHistErr(e.message || 'Failed to load history');
    }
    setHistLoading(false);
  }, [fromDate, toDate, direction]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  return (
    <div className="page-signal-alerts sa-page">
      <div className="sa-header">
        <div>
          <h1 className="sa-title">🎯 3-Min Signal Alerts</h1>
          <p className="sa-sub">
            Bullish/Bearish setup tracker · 3 closed candles outside Keltner band · VWAP · ADX &gt; 20 · Supertrend aligned
          </p>
        </div>
        <div className="sa-seg">
          <button className={tab === 'live' ? 'on' : ''} onClick={() => setTab('live')}>● Live / Today</button>
          <button className={tab === 'history' ? 'on' : ''} onClick={() => setTab('history')}>⏱ History</button>
        </div>
      </div>

      {tab === 'live' && (
        <LiveView data={live} error={liveErr} onRefresh={loadLive} />
      )}

      {tab === 'history' && (
        <HistoryView
          fromDate={fromDate} toDate={toDate} direction={direction}
          onFromChange={setFromDate} onToChange={setToDate} onDirChange={setDirection}
          onLoad={loadHistory} loading={histLoading} error={histErr}
          rows={histRows} summary={histSummary} dates={histDates}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function LiveView({ data, error, onRefresh }) {
  const active = data?.active;
  const today = data?.today || [];
  const evals = data?.evaluations || [];

  return (
    <>
      {error && <div className="sa-error">{error}</div>}

      <div className="sa-active-card">
        {active ? (
          <>
            <div className="sa-active-head">
              <DirectionBadge dir={active.direction} />
              <span className="sa-live-dot">● LIVE</span>
              <span className="sa-active-since">since {timeOnly(active.start_time)}</span>
            </div>
            <div className="sa-active-grid">
              <Stat label="Entry Spot" value={fmt(active.entry_spot)} />
              <Stat label="Strike" value={`${fmtInt(active.option_strike)} ${active.option_type}`} />
              <Stat label="Entry Premium" value={fmt(active.entry_premium)} />
              <Stat label="Lot Size" value={fmtInt(active.lot_size)} />
              <Stat label="Entry VWAP" value={fmt(active.entry_vwap)} />
              <Stat label="Entry ADX" value={fmt(active.entry_adx)} />
              <Stat label="KC Upper" value={fmt(active.entry_kc_upper)} />
              <Stat label="KC Lower" value={fmt(active.entry_kc_lower)} />
            </div>
          </>
        ) : (
          <div className="sa-active-empty">
            <h3>No active signal</h3>
            <p>Tracker is monitoring 3-min candles. A new alert will fire automatically when all bullish or bearish conditions are met.</p>
            <button className="sa-btn-ghost" onClick={onRefresh}>↻ Refresh</button>
          </div>
        )}
      </div>

      <h3 className="sa-section">Today's Alerts ({today.length})</h3>
      <SignalTable rows={today} />

      <details className="sa-evals">
        <summary>Recent rule evaluations ({evals.length})</summary>
        <table className="sa-eval-table">
          <thead>
            <tr><th>Time</th><th>Spot</th><th>KC↑</th><th>KC↓</th><th>VWAP</th><th>ADX</th><th>ST</th><th>Bull</th><th>Bear</th></tr>
          </thead>
          <tbody>
            {evals.slice().reverse().map((e, i) => (
              <tr key={i}>
                <td>{e.time}</td>
                <td>{fmt(e.spot)}</td>
                <td>{fmt(e.kc_upper)}</td>
                <td>{fmt(e.kc_lower)}</td>
                <td>{fmt(e.vwap)}</td>
                <td>{fmt(e.adx)}</td>
                <td>{fmt(e.supertrend)}</td>
                <td>{e.bullish_setup ? '✓' : '—'}</td>
                <td>{e.bearish_setup ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function HistoryView({
  fromDate, toDate, direction, onFromChange, onToChange, onDirChange,
  onLoad, loading, error, rows, summary, dates,
}) {
  return (
    <>
      <div className="sa-filter-bar">
        <label>From <input type="date" value={fromDate} onChange={(e) => onFromChange(e.target.value)} /></label>
        <label>To <input type="date" value={toDate} onChange={(e) => onToChange(e.target.value)} /></label>
        <label>Direction
          <select value={direction} onChange={(e) => onDirChange(e.target.value)}>
            <option value="">All</option>
            <option value="BULLISH">Bullish</option>
            <option value="BEARISH">Bearish</option>
          </select>
        </label>
        <button className="sa-btn-primary" onClick={onLoad} disabled={loading}>
          {loading ? 'Loading…' : 'Apply'}
        </button>
        {dates.length > 0 && (
          <span className="sa-dates-hint">{dates.length} trading days with alerts</span>
        )}
      </div>

      {error && <div className="sa-error">{error}</div>}

      {summary && (
        <div className="sa-summary">
          <SumStat label="Total Alerts" value={summary.total} />
          <SumStat label="Wins" value={summary.wins} cls="sa-pos" />
          <SumStat label="Losses" value={summary.losses} cls="sa-neg" />
          <SumStat label="Win Rate" value={`${summary.win_rate_pct}%`} />
          <SumStat label="Bullish" value={summary.bullish_count} />
          <SumStat label="Bearish" value={summary.bearish_count} />
          <SumStat label="Total Points" value={fmtSigned(summary.total_points)}
                   cls={summary.total_points > 0 ? 'sa-pos' : summary.total_points < 0 ? 'sa-neg' : ''} />
          <SumStat label="Total P&L / Lot" value={fmtSigned(summary.total_pnl_per_lot)}
                   cls={summary.total_pnl_per_lot > 0 ? 'sa-pos' : summary.total_pnl_per_lot < 0 ? 'sa-neg' : ''} />
        </div>
      )}

      <SignalTable rows={rows} showDate />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
function SignalTable({ rows, showDate }) {
  if (!rows || rows.length === 0) {
    return <div className="sa-empty">No alerts to display.</div>;
  }
  return (
    <div className="sa-table-wrap">
      <table className="sa-table">
        <thead>
          <tr>
            {showDate && <th>Date</th>}
            <th>Start</th>
            <th>End</th>
            <th>Dur (m)</th>
            <th>Dir</th>
            <th>Entry Spot</th>
            <th>Exit Spot</th>
            <th>Points</th>
            <th>Strike</th>
            <th>Entry Prem</th>
            <th>Exit Prem</th>
            <th>Δ Prem</th>
            <th>P&L / Lot</th>
            <th>Exit Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              {showDate && <td>{r.trade_date}</td>}
              <td>{timeOnly(r.start_time)}</td>
              <td>{r.end_time ? timeOnly(r.end_time) : <em>open</em>}</td>
              <td>{r.duration_minutes != null ? r.duration_minutes : '—'}</td>
              <td><DirectionBadge dir={r.direction} /></td>
              <td>{fmt(r.entry_spot)}</td>
              <td>{fmt(r.exit_spot)}</td>
              <td><PnlCell value={r.points_captured} /></td>
              <td>{r.option_strike ? `${fmtInt(r.option_strike)} ${r.option_type}` : '—'}</td>
              <td>{fmt(r.entry_premium)}</td>
              <td>{fmt(r.exit_premium)}</td>
              <td><PnlCell value={r.premium_change} /></td>
              <td><PnlCell value={r.pnl_per_lot} /></td>
              <td className="sa-reason">{r.exit_reason || (r.status === 'OPEN' ? 'In progress' : '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="sa-stat">
      <div className="sa-stat-label">{label}</div>
      <div className="sa-stat-value">{value}</div>
    </div>
  );
}
function SumStat({ label, value, cls }) {
  return (
    <div className="sa-sum">
      <div className="sa-sum-label">{label}</div>
      <div className={`sa-sum-value ${cls || ''}`}>{value}</div>
    </div>
  );
}
